/**
 * Helpers that keep StartTraining's auto-save bound to the exercise the user
 * was actually editing — preventing logged values from landing on the wrong
 * exercise (#14) and preventing the view from jumping back to the first
 * exercise after a replace (#11).
 */

/**
 * Decide which exercise a queued auto-save should write to.
 *
 * Auto-save is debounced: the user's edits belong to the exercise that was
 * active when they typed (`pendingUuid`). By the time the debounce timer
 * fires, the active exercise may have changed (navigation, exercise replace,
 * background restore). Persisting under the now-current exercise would land
 * values on the WRONG exercise.
 *
 * Rule: only commit when the exercise we captured the edits for is still the
 * one on screen. Otherwise skip — the navigation paths run an immediate save
 * before they switch exercise, so nothing is lost.
 *
 * @returns the uuid to save under, or `null` to skip the save.
 */
export function resolveAutoSaveTargetUuid(
  pendingUuid: string | null,
  currentUuid: string | null
): string | null {
  if (!pendingUuid || !currentUuid) return null;
  return pendingUuid === currentUuid ? pendingUuid : null;
}

/**
 * After replacing an exercise we reload the whole workout, which would
 * otherwise drop the user back on the first exercise. Return the index of the
 * slot the user was on, clamped to the (possibly changed) exercise count so we
 * never point past the end.
 *
 * @returns a safe index to restore to; 0 when there are no exercises.
 */
export function clampRestoreIndex(
  prevIndex: number,
  exerciseCount: number
): number {
  if (exerciseCount <= 0) return 0;
  if (prevIndex < 0) return 0;
  if (prevIndex > exerciseCount - 1) return exerciseCount - 1;
  return prevIndex;
}

/**
 * `startAt` is a one-shot instruction used when an exercise card opens the player.
 * It must never become the URL stored in a session snapshot or rest notification:
 * reusing that URL later would override `gym.currentExerciseIndex` and jump back to
 * the originally opened exercise (most often index 0).
 *
 * Keep all durable query flags (for example the race loading hint) and the hash.
 */
export function canonicalWorkoutSessionRoute(fullPath: string): string {
  if (!fullPath || !fullPath.startsWith("/start-training/")) return fullPath;

  const hashIndex = fullPath.indexOf("#");
  const hash = hashIndex >= 0 ? fullPath.slice(hashIndex) : "";
  const pathAndQuery = hashIndex >= 0 ? fullPath.slice(0, hashIndex) : fullPath;
  const queryIndex = pathAndQuery.indexOf("?");

  if (queryIndex < 0) return fullPath;

  const path = pathAndQuery.slice(0, queryIndex);
  const query = new URLSearchParams(pathAndQuery.slice(queryIndex + 1));
  query.delete("startAt");

  const durableQuery = query.toString();
  return `${path}${durableQuery ? `?${durableQuery}` : ""}${hash}`;
}
